// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect, useState } from 'react';

import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import {
    Box, Button, Card, CardActions, CardContent, CardHeader, CardMedia, Checkbox, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, FormControlLabel, List, ListItem, Skeleton, styled,
    Typography
} from '@mui/material';

import TrophyIcon from '../../assets/img/trophy.svg';
import { updateContactLeaderboardConsent } from '../../slices/contactSlice/contact';
import {
    resetLeaderboardConsentState, updateLeaderboardConsent
} from '../../slices/leaderboardSlice/leaderboardConsent';
import { RootState, useAppDispatch, useAppSelector } from '../../slices/store';
import {
    CertificateHolder, GetUserLeaderboardRequestState, UserLeaderboardData
} from '../../utils/types';
import { contactDetails, TOP_CERTIFICATE_HOLDERS_COUNT } from '../../config/constant';

const StyledListItem = styled(ListItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0.75),
  marginBottom: theme.spacing(1),
  borderRadius: 10,
  border: `1px solid ${theme.palette.divider}`,
  transition: "all 0.3s ease",
  textTransform: "capitalize",
}));

const UserLeaderboard: React.FC<{
  data: GetUserLeaderboardRequestState;
}> = ({ data }) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [hasLeaderboardConsent, setHasLeaderboardConsent] =
    useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [pendingConsent, setPendingConsent] = useState<boolean | null>(null);
  const [title, setTitle] = React.useState<string>("Leaderboard");
  const contactState = useAppSelector((state: RootState) => state.contact);
  const updateLeaderboardConsentState = useAppSelector((state: RootState) => state.leaderboardConsent);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!data.backgroundProcess) {
      const response = data.response as UserLeaderboardData;
      if (response.currentQuarterLeaderboard) {
        setTitle(
          `Leaderboard (${response.currentQuarterLeaderboard.quarter} ${response.currentQuarterLeaderboard.year})`
        );
      }
    }
  }, [data]);

  useEffect(() => {
    if (contactState.state === "success") {
      setHasLeaderboardConsent(
        contactState.response.certificationLeaderboardConsent
      );
    }
  }, [contactState]);

  useEffect(() => {
    if (updateLeaderboardConsentState.state === "success") {
      setHasLeaderboardConsent(pendingConsent as boolean);
      dispatch(updateContactLeaderboardConsent(pendingConsent));
      dispatch(resetLeaderboardConsentState());
      setShowDialog(false);
      setIsUpdating(false);
      setPendingConsent(null);
    }
  }, [updateLeaderboardConsentState]);

  const getTrophyColor = (index: number): string => {
    switch (index) {
      case 0:
        return "#FFD700"; // Gold
      case 1:
        return "#C0C0C0"; // Silver
      case 2:
        return "#CD7F32"; // Bronze
      default:
        return "inherit";
    }
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    // Store the pending consent value
    setPendingConsent(event.target.checked);
    setShowDialog(true);
  };

  const handleDialogClose = (): void => {
    setShowDialog(false);
    setPendingConsent(null);
  };

  const handleConfirm = async (): Promise<void> => {
    if (pendingConsent === null) return;
    setIsUpdating(true);
    try {
      await dispatch(
        updateLeaderboardConsent({
          certificateID: contactState.response.certificationId,
          consent: {
            consent: pendingConsent,
          },
        })
      );
    } catch (error) {
      setHasLeaderboardConsent(hasLeaderboardConsent);
      setIsUpdating(false);
      setShowDialog(false);
      setPendingConsent(null);
    }
  };

  const renderEmptyQuarterMessage = () => (
    <>
      <Box sx={{ mb: 4.7 }}>
        <Typography sx={{ mt: 4 }} color="text.secondary">
          No certifications have been recorded for this quarter yet.
        </Typography>
      </Box>
      <ul>
        <Typography component="li" variant="subtitle1" align="left">
          Be the first one to get certified!
        </Typography>
      </ul>
    </>
  );

  const renderSkeletonItem = () => (
    <StyledListItem>
      <Skeleton
        variant="circular"
        width={24}
        height={24}
        sx={{ ml: 1.5, mr: 2 }}
      />
      <Box sx={{ flexGrow: 1, textAlign: "left" }}>
        <Skeleton variant="text" width="60%" />
      </Box>
      <Skeleton variant="text" width={40} sx={{ pr: 2 }} />
    </StyledListItem>
  );

  const renderSkeletonWithOverlay = () => (
    <Box sx={{ position: "relative", mt: 1.1 }}>
      <List disablePadding>
        {[...Array(TOP_CERTIFICATE_HOLDERS_COUNT + 2)].map((_, index) => (
          <React.Fragment key={index}>{<Box sx={{my: -0.5}}>{renderSkeletonItem()}</Box>}</React.Fragment>
        ))}
      </List>
    </Box>
  );

  const renderUserPosition = () => {
    if (
      !data.response.currentQuarterLeaderboard &&
      !data.response.userPosition
    ) {
      return renderEmptyQuarterMessage();
    }
    if (data.response.userPosition && (data.response.userPosition.position ?? -1) <= TOP_CERTIFICATE_HOLDERS_COUNT) {
      return null;  
    }

    let userPosition: CertificateHolder|null|undefined = data.response.userPosition;
    return (
      <>
        {renderLeaderboardItem(userPosition, data.response.userPosition?.position ? data.response.userPosition.position - 1 : -1)}
      </>
    );
  };
  
  const renderLeaderboardItem = (holder: CertificateHolder|null|undefined, index: number) => {
    if (!holder) {
      return (
        <StyledListItem key={index}>
          <Typography variant="body2" fontWeight="bold" color="primary" sx={{ ml: 1.5, mr: 2 }}>
            N/A
          </Typography>
          <Box sx={{ flexGrow: 1, textAlign: "left" }}>
            <Typography variant="body2" fontWeight="light">
              You
            </Typography>
          </Box>
          <Typography variant="body2" fontWeight="bold" color="primary" sx={{ pr: 2 }}>
            N/A
          </Typography>
        </StyledListItem>
      );
    }

    const currentUserPosition = data.response.userPosition?.position;

    const isCurrentUser = data.response.userPosition && 
      data.response.userPosition.position === index + 1;
  
    return (
      <StyledListItem
        key={index}
        sx={{
          backgroundColor: isCurrentUser ? "rgba(255, 215, 0, 0.10)" : {},
          border: isCurrentUser ? "1px solid #ff7300" : {},
          py: currentUserPosition && currentUserPosition <= TOP_CERTIFICATE_HOLDERS_COUNT ? 2 : {},
          my: currentUserPosition && currentUserPosition <= TOP_CERTIFICATE_HOLDERS_COUNT ? 1.3 : {}
        }}         
      >
        { isCurrentUser && (data.response.userPosition?.position ?? -1) > TOP_CERTIFICATE_HOLDERS_COUNT ? (
          <Typography variant="body2" fontWeight="bold" color="primary" sx={{ ml: 1.5, mr: 2 }}>
            {data.response.userPosition?.position}
          </Typography>
        ):(
        <EmojiEventsIcon
          sx={{ color: getTrophyColor(index), ml: 1.5, mr: 2, fontSize: 24 }}
        />
        )
        }
        <Box sx={{ flexGrow: 1, textAlign: "left" }}>
          <Typography variant="body2" fontWeight="light">
            {holder.fullName} {isCurrentUser ? "(You)" : ""}
          </Typography>
        </Box>
        <Typography variant="body2" fontWeight="bold" color="primary" sx={{ pr: 2 }}>
          {holder.marks} pts
        </Typography>
      </StyledListItem>
    );
  };

  const renderLeaderboard = () => {
    if (!data.response.currentQuarterLeaderboard?.topCertificateHolders) {
      return null;
    }

    const topHolders =
      data.response.currentQuarterLeaderboard.topCertificateHolders;
    const remainingSkeletons = TOP_CERTIFICATE_HOLDERS_COUNT - topHolders.length;

    return (
      <List disablePadding sx={{ mt: 1.1 }}>
        {topHolders.map((holder, index) =>
          renderLeaderboardItem(holder, index)
        )}
        {remainingSkeletons > 0 &&
          [...Array(remainingSkeletons)].map((_, index) => (
            <React.Fragment key={`skeleton-${index}`}>
              {renderSkeletonItem()}
            </React.Fragment>
          ))}
      </List>
    );
  };

  return (
    <Card
      square
      sx={{
        boxShadow: "2px 5px 10px 0px rgb(0 0 0 / 20%)",
        p: 3,
        pb: 0,
        mb: 3,
        width: "100%",
      }}
    >
      <CardMedia
        component="img"
        sx={{ width: 100, ml: 2 }}
        image={TrophyIcon}
      />
      <CardHeader
        title={title}
        titleTypographyProps={{
          align: "left",
        }}
        subheaderTypographyProps={{
          align: "right",
        }}
      />
      <Divider variant="middle" />
      <CardContent sx={{ mt: 1, pt:1 }}>
      <Box sx={{ mt: -1.5, mb: -0.5  }}>
          <FormControlLabel
            sx={{ pl:0.5 }}
            control={
              <Checkbox
                checked={hasLeaderboardConsent}
                onChange={handleCheckboxChange}
                size="small"
              />
            }
            label={
              <Typography variant="caption">
                Show my name in public leaderboard
              </Typography>
            }
          />
        </Box>
        {data.backgroundProcess || data.state !== "success" ? (
          renderSkeletonWithOverlay()
        ) : (
          <>
          {renderLeaderboard()}
            <>
              {data.response.currentQuarterLeaderboard &&
              (!data.response.userPosition || 
                (data.response.userPosition.position ?? -1) > TOP_CERTIFICATE_HOLDERS_COUNT) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'grey.400' }} />
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'grey.400' }} />
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'grey.400' }} />
                  </Box>
                </Box>
              )}
            </>
            {renderUserPosition()}
        </>
        )}
        <Dialog
          open={showDialog}
          onClose={handleDialogClose}
          disableEscapeKeyDown={isUpdating}
        >
          <DialogTitle>Confirmation</DialogTitle>
          <DialogContent>
            <Typography>
              {pendingConsent
                ? "Are you sure you want to display your full name on the public leaderboard?"
                : "Are you sure you want to hide your name from the public leaderboard?"}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} disabled={isUpdating}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              disabled={isUpdating}
            >
              {isUpdating ? "Updating..." : "Continue"}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
      {data.state === "success" && !data.response.currentQuarterLeaderboard &&(
      <CardActions>
        <Button
          onClick={() => {
            window.open(contactDetails.certificationWebUrl, "_blank");
          }}
          variant="contained"
          sx={{ width: { xs: '80%', md: '100%' } , mb : 3 }}
        >
          Get Certified
        </Button>
      </CardActions>
      )}
    </Card>
  );
};

export default UserLeaderboard;
